import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * UnifiedModal — canonical modal/dialog primitive.
 *
 * Props:
 *  - open: boolean
 *  - onClose: fn
 *  - title: string | node
 *  - size: 'sm' | 'md' | 'lg' | 'xl'
 *  - children: body
 *  - footer: optional footer node (typically buttons)
 *  - closeOnOverlay: boolean (default true)
 */
export const UnifiedModal = ({
  open,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  closeOnOverlay = true,
  testId,
}) => {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 400, md: 560, lg: 760, xl: 960 };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid={testId}
      onClick={(e) => {
        if (closeOnOverlay && e.target === e.currentTarget && onClose) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--mesp-overlay)',
        backdropFilter: 'blur(4px)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--mesp-space-4)',
      }}
      className="mesp-ui"
    >
      <div
        style={{
          background: 'var(--mesp-bg-elevated)',
          border: '1px solid var(--mesp-border)',
          borderRadius: 'var(--mesp-radius-lg)',
          boxShadow: 'var(--mesp-shadow-modal)',
          width: '100%',
          maxWidth: widths[size],
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          color: 'var(--mesp-text)',
        }}
      >
        {(title || onClose) && (
          <div
            style={{
              padding: 'var(--mesp-space-5) var(--mesp-space-6)',
              borderBottom: '1px solid var(--mesp-divider)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--mesp-space-3)',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--mesp-font-h3)',
                fontWeight: 'var(--mesp-font-weight-semibold)',
                color: 'var(--mesp-text)',
              }}
              data-testid={testId ? `${testId}-title` : undefined}
            >
              {title}
            </h3>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="إغلاق"
                data-testid={testId ? `${testId}-close` : 'unified-modal-close'}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--mesp-text-muted)',
                  padding: 6,
                  borderRadius: 'var(--mesp-radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color var(--mesp-trans-fast), background var(--mesp-trans-fast)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--mesp-text)'; e.currentTarget.style.background = 'var(--mesp-bg-sunken)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--mesp-text-muted)'; e.currentTarget.style.background = 'transparent'; }}
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        <div style={{ padding: 'var(--mesp-space-6)', overflowY: 'auto', flex: 1 }}>
          {children}
        </div>

        {footer && (
          <div
            style={{
              padding: 'var(--mesp-space-4) var(--mesp-space-6)',
              borderTop: '1px solid var(--mesp-divider)',
              display: 'flex',
              gap: 'var(--mesp-space-3)',
              justifyContent: 'flex-end',
              background: 'var(--mesp-bg-sunken)',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default UnifiedModal;
